'use client';
import { TZDate } from '@date-fns/tz';
import { addDays, format, startOfDay, subDays } from 'date-fns';
import { type FC, useMemo, useState } from 'react';
import { cn } from '@/utils/cn';

interface ICalendarProps {
  onChange: (date: TZDate) => void;
  type?: 'past' | 'future';
  showOnlyFirstDay?: boolean;
}

interface DateItem {
  label: string;
  date: string;
  fullDate: TZDate;
}

const Calendar: FC<ICalendarProps> = ({
  onChange,
  type = 'future',
  showOnlyFirstDay = false,
}) => {
  // Generate dates once using useMemo
  const dates = useMemo((): DateItem[] => {
    const today = startOfDay(new TZDate(new Date(), 'Europe/Prague'));

    if (type === 'future') {
      return Array.from({ length: showOnlyFirstDay ? 1 : 5 }, (_, index) => {
        const date =
          index === 0
            ? today
            : new TZDate(startOfDay(addDays(today, index)), 'Europe/Prague');
        return {
          label: index === 0 ? 'TODAY' : format(date, 'EEE'),
          date: format(date, 'd MMM'),
          fullDate: date,
        };
      });
    }
    return Array.from({ length: showOnlyFirstDay ? 1 : 5 }, (_, index) => {
      const daysFromEnd = 4 - index;
      const date =
        daysFromEnd === 0
          ? today
          : new TZDate(
              startOfDay(subDays(today, daysFromEnd)),
              'Europe/Prague'
            );
      return {
        label: daysFromEnd === 0 ? 'TODAY' : format(date, 'EEE'),
        date: format(date, 'd MMM'),
        fullDate: date,
      };
    });
  }, [type, showOnlyFirstDay]);

  const [activeDate, setActiveDate] = useState<string>(() => {
    const today = startOfDay(new TZDate(new Date(), 'Europe/Prague'));
    return format(today, 'd MMM');
  });

  return (
    <div className={cn('flex cursor-pointer flex-row justify-between')}>
      {dates.map((dateItem) => {
        const isActive = format(dateItem.fullDate, 'd MMM') === activeDate;

        return (
          <button
            aria-current={isActive ? 'date' : undefined}
            aria-label={`Select ${dateItem.label}, ${dateItem.date}`}
            className={cn(
              'flex h-[40px] flex-1 flex-col items-center justify-center px-[2px] py-[4px]',
              showOnlyFirstDay
                ? 'rounded-lg'
                : 'first:rounded-l-lg last:rounded-r-lg',
              isActive
                ? 'bg-light-blue font-medium text-black'
                : 'bg-light-gray text-white hover:bg-light-blue-o15',
              showOnlyFirstDay && 'max-w-[120px]'
            )}
            key={dateItem.fullDate.toISOString()}
            onClick={() => {
              setActiveDate(format(dateItem.fullDate, 'd MMM'));
              onChange(dateItem.fullDate);
            }}
            type="button"
          >
            <p className={cn('text-sm', isActive && 'font-bold')}>
              {dateItem.label}
            </p>
            <p className="text-[10px]">{dateItem.date}</p>
          </button>
        );
      })}
    </div>
  );
};

export default Calendar;
